import { useState, HTMLAttributes } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "./utils";

interface CodeBlockProps extends HTMLAttributes<HTMLPreElement> {
  code: string;
  language?: string;
  showLineNumbers?: boolean;
}

export function CodeBlock({
  code,
  language = "typescript",
  showLineNumbers = false,
  className,
  ...props
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn("relative group rounded-md overflow-hidden border border-border bg-[#18181b] dark:bg-[#0c0c0e]", className)}>
      <div className="flex items-center justify-between px-4 py-1.5 bg-[#27272a] dark:bg-[#18181b] border-b border-white/5">
        <span className="text-xs font-mono text-zinc-400 select-none">
          {language}
        </span>
        <button
          onClick={handleCopy}
          className="text-zinc-400 hover:text-white transition-colors p-1"
          aria-label="Copy code"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="overflow-x-auto p-4 max-h-[400px]">
        <pre
          className={cn(
            "text-sm font-mono text-zinc-100 leading-relaxed",
            showLineNumbers && "pl-8 [counter-reset:line]"
          )}
          {...props}
        >
          <code className="relative block">
            {showLineNumbers
              ? code.split("\n").map((line, i) => (
                  <span
                    key={i}
                    className="block hover:bg-white/5 px-2 -mx-2 before:absolute before:-left-6 before:text-zinc-500 before:content-[counter(line)] before:[counter-increment:line]"
                  >
                    {line || " "}
                  </span>
                ))
              : code}
          </code>
        </pre>
      </div>
    </div>
  );
}
